<template>
  <div
    ref="rootRef"
    class="colorpicker"
  >
    <button
      ref="triggerRef"
      type="button"
      class="colorpicker__trigger"
      :style="large ? largeTriggerStyle : undefined"
      :aria-label="`Color picker${color ? `, current ${color}` : ''}`"
      @click="togglePicker"
    >
      <span
        class="colorpicker__swatch"
        :style="{ background: previewColor }"
      />
      <span
        class="colorpicker__value"
        :style="large ? largeValueStyle : undefined"
      >
        {{ displayHex || 'Select color' }}
      </span>
    </button>

    <div
      v-show="isOpen"
      ref="popoverRef"
      class="colorpicker__popover"
      :style="popoverStyle"
      @mousedown.stop
      @click.stop
    >
      <div
        class="colorpicker__dragHandle"
        @mousedown.stop.prevent="startDrag"
      >
        Drag to move
      </div>

      <Vue3ColorPicker
        :modelValue="pickerValue"
        mode="solid"
        :showColorList="false"
        :showPickerMode="false"
        :showAlpha="true"
        :showEyeDrop="true"
        :showInputMenu="false"
        :showInputSet="true"
        :showButtons="showButtons"
        type="RGBA"
        inputType="RGB"
        theme="dark"
        @onChange="handleUpdate"
        @onSave="handleSave"
        @onCancel="handleCancel"
      />

    </div>
  </div>
</template>

<script>
import { onBeforeUnmount, onMounted, computed, ref, watch } from 'vue';
import { Vue3ColorPicker } from '@cyhnkckali/vue3-color-picker';

export default {
  components: {
    Vue3ColorPicker,
  },

  props: {
    color: {
      required: true,
    },
    showButtons: {
      type: Boolean,
      default: false,
    },
    large: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['change', 'save', 'cancel'],

  setup(props, { emit }) {
    const rootRef = ref(null);
    const triggerRef = ref(null);
    const popoverRef = ref(null);
    const isOpen = ref(false);
    const pickerValue = ref(props.color || '#000000');
    const dragState = ref({
      active: false,
      offsetX: 0,
      offsetY: 0,
    });

    const previewColor = computed(() => pickerValue.value || props.color || '#000000');
    const largeTriggerStyle = computed(() => ({
      padding: '6px 8px',
      gap: '12px',
    }));
    const largeValueStyle = computed(() => ({
      fontSize: '12px',
    }));
    const normalizeColor = (value) => {
      const raw = String(value || '').trim();
      if (!raw) {
        return '';
      }

      if (raw.startsWith('#')) {
        return raw;
      }

      const rgbMatch = raw.match(/rgba?\(([^)]+)\)/i);
      if (!rgbMatch) {
        return raw;
      }

      const channels = rgbMatch[1]
        .split(',')
        .map((part) => part.trim());

      const [r, g, b] = channels
        .slice(0, 3)
        .map((part) => {
          const numeric = Number.parseInt(part.trim(), 10);
          return Number.isNaN(numeric) ? 0 : numeric;
        });

      const alpha = channels[3] !== undefined ? Number.parseFloat(channels[3]) : 1;
      if (!Number.isNaN(alpha) && alpha < 1) {
        return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, alpha))})`;
      }

      const hex = [r, g, b]
        .map((channel) => channel.toString(16).padStart(2, '0'))
        .join('')
        .toUpperCase();

      return `#${hex}`;
    };
    const displayHex = computed(() => {
      const value = String(pickerValue.value || props.color || '').trim();
      if (!value) {
        return '';
      }

      if (value.startsWith('#')) {
        return value;
      }

      return normalizeColor(value) || value;
    });
    const mode = computed(() => (String(pickerValue.value).includes('gradient') ? 'gradient' : 'solid'));
    const popoverStyle = ref({});
    const syncPopoverPosition = () => {
      if (!rootRef.value || !triggerRef.value) {
        return;
      }

      const rect = triggerRef.value.getBoundingClientRect();
      const popoverHeight = popoverRef.value?.offsetHeight || 420;
      const openAbove = rect.top > popoverHeight + 16;
      const maxTop = window.innerHeight - 24;
      const top = openAbove
        ? rect.top - popoverHeight - 8
        : rect.bottom + 8;

      popoverStyle.value = {
        position: 'fixed',
        top: `${Math.max(8, Math.min(top, maxTop))}px`,
        left: `${Math.max(8, Math.min(rect.left, window.innerWidth - 24))}px`,
        zIndex: 1000,
      };
    };

    const startDrag = (event) => {
      dragState.value.active = true;
      dragState.value.offsetX = event.clientX - (parseFloat(popoverStyle.value.left) || 0);
      dragState.value.offsetY = event.clientY - (parseFloat(popoverStyle.value.top) || 0);
    };

    const handleDocumentMouseMove = (event) => {
      if (!dragState.value.active) {
        return;
      }

      popoverStyle.value = {
        ...popoverStyle.value,
        left: `${Math.max(8, Math.min(event.clientX - dragState.value.offsetX, window.innerWidth - 24))}px`,
        top: `${Math.max(8, Math.min(event.clientY - dragState.value.offsetY, window.innerHeight - 24))}px`,
      };
    };

    const endDrag = () => {
      dragState.value.active = false;
    };

    const openPicker = () => {
      if (!isOpen.value) {
        isOpen.value = true;
        requestAnimationFrame(syncPopoverPosition);
      }
    };

    const closePicker = () => {
      isOpen.value = false;
    };

    const togglePicker = () => {
      if (isOpen.value) {
        closePicker();
      } else {
        openPicker();
      }
    };

    const handleDocumentMouseDown = (event) => {
      if (rootRef.value && !rootRef.value.contains(event.target)) {
        if (isOpen.value && props.showButtons) {
          handleCancel();
        } else {
          closePicker();
        }
      }
    };

    const handleUpdate = (value) => {
      const normalized = normalizeColor(value || '');
      pickerValue.value = normalized || value || '';
      emit('change', normalized || value || '');
    };

    const handleSave = (value) => {
      const normalized = normalizeColor(value || pickerValue.value || props.color || '');
      pickerValue.value = normalized || pickerValue.value;
      emit('save', normalized || pickerValue.value || props.color || '');
      closePicker();
    };

    const handleCancel = () => {
      pickerValue.value = props.color || '#000000';
      emit('cancel', props.color || '#000000');
      closePicker();
    };

    watch(
      () => props.color,
      (newColor) => {
        pickerValue.value = newColor || '#000000';
      },
    );

    onMounted(() => {
      document.addEventListener('mousedown', handleDocumentMouseDown);
      document.addEventListener('mousemove', handleDocumentMouseMove);
      document.addEventListener('mouseup', endDrag);
      window.addEventListener('resize', syncPopoverPosition);
      window.addEventListener('scroll', syncPopoverPosition, true);
    });

    onBeforeUnmount(() => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      document.removeEventListener('mousemove', handleDocumentMouseMove);
      document.removeEventListener('mouseup', endDrag);
      window.removeEventListener('resize', syncPopoverPosition);
      window.removeEventListener('scroll', syncPopoverPosition, true);
    });

    return {
      closePicker,
      handleUpdate,
      handleSave,
      handleCancel,
      isOpen,
      mode,
      openPicker,
      pickerValue,
      popoverStyle,
      previewColor,
      displayHex,
      largeTriggerStyle,
      largeValueStyle,
      rootRef,
      popoverRef,
      startDrag,
      togglePicker,
      triggerRef,
    };
  },
};
</script>

<style lang="scss">
.colorpicker {
  position: relative;
  display: inline-flex;
}

.colorpicker__trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.3125rem;
  min-width: 6.25rem;
  padding: 0.25rem 0.3125rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 0.25rem;
  background: rgba(255, 255, 255, 0.04);
  color: inherit;
  cursor: pointer;
}

.colorpicker__trigger:hover {
  border-color: rgba(255, 255, 255, 0.24);
}

.colorpicker__swatch {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.25);
  flex: 0 0 auto;
}

.colorpicker__value {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.75rem;
}

.colorpicker__popover {
  padding: 0.3125rem;
  border-radius: 0.5rem;
  background: #1f1f1f;
  box-shadow: 0 0.75rem 1.75rem rgba(0, 0, 0, 0.32);
  transform-origin: top left;
  width: fit-content;
  max-width: calc(100vw - 1rem);
  max-height: calc(100vh - 1rem);
  overflow: auto;
  z-index: 1000;
}

.colorpicker__popover :deep(.ck-cp-buttons) {
  padding: 10px 10px 0;
}

.colorpicker__dragHandle {
  padding: 0.375rem 0.5rem;
  margin-bottom: 0.25rem;
  border-radius: 0.375rem;
  color: rgba(255, 255, 255, 0.65);
  background: rgba(255, 255, 255, 0.04);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: move;
  user-select: none;
}

</style>
